import React, { useState } from "react";
import { api, formatApiErrorDetail, toLocalInput, fromLocalInput } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TEXT_FIELDS = [
  { key: "tail_number", label: "Prefixo", mono: true },
  { key: "aircraft_model", label: "Modelo da Aeronave" },
  { key: "anac_code", label: "Cód. ANAC Aeronave", mono: true },
  { key: "client_name", label: "Cliente" },
  { key: "commander_name", label: "Comandante" },
  { key: "pilot_anac_code", label: "ANAC do Piloto", mono: true },
  { key: "flight_origin", label: "Origem", mono: true },
  { key: "flight_destination", label: "Destino", mono: true },
];

export function EditWorkOrderDialog({ wo, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const openDialog = () => {
    setForm({
      tail_number: wo.tail_number || "",
      aircraft_model: wo.aircraft_model || "",
      anac_code: wo.anac_code || "",
      client_name: wo.client_name || "",
      commander_name: wo.commander_name || "",
      pilot_anac_code: wo.pilot_anac_code || "",
      flight_origin: wo.flight_origin || "",
      flight_destination: wo.flight_destination || "",
      passenger_count: wo.passenger_count ?? 0,
      arrival_datetime: toLocalInput(wo.arrival_datetime),
      estimated_departure: toLocalInput(wo.estimated_departure),
      general_notes: wo.general_notes || "",
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/work-orders/${wo.id}/office`, {
        ...form,
        passenger_count: Number(form.passenger_count) || 0,
        arrival_datetime: form.arrival_datetime ? fromLocalInput(form.arrival_datetime) : wo.arrival_datetime,
        estimated_departure: form.estimated_departure ? fromLocalInput(form.estimated_departure) : null,
      });
      toast.success("Dados da OS atualizados");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" className="h-11 rounded-sm" onClick={openDialog} data-testid="edit-wo-button">
        <Pencil className="w-4 h-4 mr-2" /> Editar dados
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">Editar dados da OS {wo.code}</DialogTitle>
            <DialogDescription>Disponível para Admin e Escritório enquanto a OS não estiver faturada.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TEXT_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">{f.label}</Label>
                  <Input
                    value={form[f.key]}
                    onChange={(e) => set(f.key, f.mono ? e.target.value.toUpperCase() : e.target.value)}
                    className={`mt-1 h-11 rounded-sm ${f.mono ? "font-mono uppercase" : ""}`}
                    data-testid={`edit-wo-${f.key}`}
                  />
                </div>
              ))}
              <div>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Passageiros</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.passenger_count}
                  onChange={(e) => set("passenger_count", e.target.value)}
                  className="mt-1 h-11 rounded-sm"
                  data-testid="edit-wo-passenger_count"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Chegada</Label>
                <Input
                  type="datetime-local"
                  value={form.arrival_datetime}
                  onChange={(e) => set("arrival_datetime", e.target.value)}
                  className="mt-1 h-11 rounded-sm"
                  data-testid="edit-wo-arrival_datetime"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Partida Estimada</Label>
                <Input
                  type="datetime-local"
                  value={form.estimated_departure}
                  onChange={(e) => set("estimated_departure", e.target.value)}
                  className="mt-1 h-11 rounded-sm"
                  data-testid="edit-wo-estimated_departure"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-600">Observações Gerais</Label>
                <Textarea
                  value={form.general_notes}
                  onChange={(e) => set("general_notes", e.target.value)}
                  className="mt-1 rounded-sm min-h-20"
                  data-testid="edit-wo-general_notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm h-11" onClick={() => setOpen(false)} data-testid="edit-wo-cancel">
              Cancelar
            </Button>
            <Button
              className="rounded-sm h-11 bg-[#0052CC] hover:bg-[#003D99] text-white"
              disabled={saving}
              onClick={save}
              data-testid="edit-wo-save"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
